/**
 * LiveAdPackView — React-style skeleton-hydration UI for in-flight ad packs.
 *
 * Subscribes to the ``adpacks_live`` Firestore collection (decoupled
 * from the canonical pack bundle) and renders one card per ad slot.
 * Each card hydrates progressively as the pipeline emits state machine
 * transitions:
 *
 *   pending      → grey skeleton placeholder
 *   brief_ready  → headline + hook text appears, image still skeleton
 *   image_preview→ image-area shows shimmer overlay (file generated, not yet uploaded)
 *   image_ready  → real image visible (post-GCS-publish)
 *   reviewed     → score badge appears
 *   final        → CTA + asset link enabled
 *   failed       → red error overlay
 *
 * The expected_ads count drives placeholder count from first paint, so
 * the layout doesn't reflow as ads start streaming in.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Ban, Check, Image as ImageIcon, Loader2, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import {
  hydrationProgress,
  subscribeToLiveAdPackCombined,
  type CombinedSnapshot,
  type LiveAdDoc,
  type LiveAdState,
} from '@/services/liveAdPackListener';
import { getApiBase } from '@/services/api';

const AGENT_BASE_URL = getApiBase();

/**
 * Resolve a backend-emitted asset URL to something the browser can load.
 *
 *   - https?:// URLs            → returned as-is (GCS public, etc.)
 *   - /local-assets/{path}     → prefixed with AGENT_BASE_URL (dev mode)
 *   - local://… or empty/null  → null (UI shows spinner / placeholder)
 */
function resolveAssetUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (raw.startsWith('local://')) return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/')) return `${AGENT_BASE_URL.replace(/\/$/, '')}${raw}`;
  return raw;
}

interface LiveAdPackViewProps {
  jobId: string;
  /** Optional click handler when an ad becomes drillable (final state). */
  onAdClick?: (ad: LiveAdDoc) => void;
  /** Compact mode: smaller cards for the BriefsView lens. */
  compact?: boolean;
  className?: string;
  /**
   * Called once when the Firestore live-pack document transitions to
   * 'completed'. Use this to immediately refresh the parent job list so the
   * job card doesn't lag behind the live view showing "Pack complete".
   */
  onComplete?: () => void;
}

const STATE_LABEL: Record<LiveAdState, string> = {
  pending: 'Queued',
  brief_ready: 'Brief',
  image_preview: 'Rendering',
  image_ready: 'Image',
  reviewed: 'Reviewed',
  final: 'Final',
  failed: 'Failed',
};

const STATE_BADGE_CLS: Record<LiveAdState, string> = {
  pending: 'bg-slate-100 text-slate-500 border-slate-200',
  brief_ready: 'bg-blue-50 text-blue-700 border-blue-200',
  image_preview: 'bg-violet-50 text-violet-700 border-violet-200',
  image_ready: 'bg-teal-50 text-teal-700 border-teal-200',
  reviewed: 'bg-amber-50 text-amber-700 border-amber-200',
  final: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
};

export function LiveAdPackView({ jobId, onAdClick, compact = false, className, onComplete }: LiveAdPackViewProps) {
  const [snapshot, setSnapshot] = useState<CombinedSnapshot>({
    pack: null,
    ads: {},
    adsOrdered: [],
    source: 'none',
  });

  useEffect(() => {
    if (!jobId) return;
    // Combined subscription — Firestore primary, HTTP polling fallback
    // for environments where Firestore reads are blocked (security
    // rules in dev, project mismatch, etc.). Same shape as a regular
    // snapshot so the rest of this component is unchanged.
    const unsub = subscribeToLiveAdPackCombined(jobId, setSnapshot);
    return () => unsub();
  }, [jobId]);

  const progress = hydrationProgress(snapshot);
  const expected = snapshot.pack?.expected_ads ?? 0;
  const status = snapshot.pack?.status ?? 'pending';

  // Fire onComplete once when the live pack doc first transitions to
  // 'completed'. This lets the parent immediately refresh job status so the
  // LocalJobCard doesn't show "RUNNING" while the grid shows "Pack complete".
  const completedFiredRef = useRef(false);
  useEffect(() => {
    if (status === 'completed' && !completedFiredRef.current && onComplete) {
      completedFiredRef.current = true;
      onComplete();
    }
  }, [status, onComplete]);
  const phase = snapshot.pack?.current_phase;
  const message = snapshot.pack?.pipeline_message;

  // Build the slot grid: union of (a) ads we've received and (b) empty
  // placeholders up to ``expected_ads``. Sorted by idx so layout is stable.
  const slots = useMemo(() => {
    const out: Array<{ idx: number; ad: LiveAdDoc | null }> = [];
    const seenIdxs = new Set(snapshot.adsOrdered.map((a) => a.idx));
    snapshot.adsOrdered.forEach((ad) => out.push({ idx: ad.idx, ad }));
    for (let i = 0; i < expected; i++) {
      if (!seenIdxs.has(i)) out.push({ idx: i, ad: null });
    }
    out.sort((a, b) => a.idx - b.idx);
    return out;
  }, [snapshot.adsOrdered, expected]);

  if (!snapshot.pack && expected === 0) {
    // No live doc yet — pipeline hasn't emitted init. Show a thin loading bar.
    return (
      <div className={clsx('rounded-lg border border-slate-200 bg-white p-4', className)}>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 size={14} className="animate-spin" />
          Waiting for pipeline to start…
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('rounded-lg border border-slate-200 bg-white', className)}>
      {/* Header — pack-level status bar */}
      <div className="flex items-center justify-between p-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          {status === 'completed' ? (
            <Check size={14} className="text-emerald-600" />
          ) : status === 'failed' ? (
            <AlertCircle size={14} className="text-rose-600" />
          ) : status === 'cancelled' ? (
            <Ban size={14} className="text-slate-500" />
          ) : (
            <Sparkles size={14} className="text-violet-500 animate-pulse" />
          )}
          <span className="text-sm font-semibold text-slate-700">
            {status === 'completed'
              ? 'Pack complete'
              : status === 'failed'
                ? 'Pack failed'
                : status === 'cancelled'
                  ? 'Pack cancelled'
                  : 'Generating live'}
          </span>
          {phase && status === 'running' && (
            <span className="text-xs text-slate-500">· {phase}</span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {snapshot.adsOrdered.filter((a) => a.state === 'final').length} of {expected} done
        </div>
      </div>

      {/* Aggregate hydration progress bar */}
      <div className="px-3 pt-2">
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500',
              status === 'failed'
                ? 'bg-rose-400'
                : status === 'cancelled'
                  ? 'bg-slate-300'
                  : 'bg-gradient-to-r from-violet-400 via-teal-400 to-emerald-400',
            )}
            style={{ width: `${Math.max(progress * 100, 3)}%` }}
          />
        </div>
        {message && status === 'running' && (
          <p className="text-[11px] text-slate-500 mt-1.5 italic">{message}</p>
        )}
      </div>

      {/* Slot grid */}
      <div
        className={clsx(
          'grid gap-3 p-3',
          compact ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
        )}
      >
        {slots.map(({ idx, ad }) => (
          <LiveAdCard
            key={ad?.ad_id ?? `placeholder-${idx}`}
            ad={ad}
            slotIdx={idx}
            compact={compact}
            onClick={ad && ad.state === 'final' && onAdClick ? () => onAdClick(ad) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LiveAdCard — single ad slot, hydrates per state-machine transition.
// ---------------------------------------------------------------------------

interface LiveAdCardProps {
  ad: LiveAdDoc | null;
  slotIdx: number;
  compact: boolean;
  onClick?: () => void;
}

function LiveAdCard({ ad, slotIdx, compact, onClick }: LiveAdCardProps) {
  const state = ad?.state ?? 'pending';
  const isFailed = state === 'failed';
  const isFinal = state === 'final';
  const hasImage = state === 'image_ready' || state === 'reviewed' || state === 'final';
  const hasPreview = state === 'image_preview' || hasImage;
  // When we have a real thumbnail (data: URL inline) treat it as real
  // pixel content even at image_preview — no opacity dim.
  const hasThumbnail = !!ad?.preview_data_url;

  // The image src cascades, preferring high-fidelity sources:
  //   1. asset_url        — post-publish public or /local-assets URL
  //   2. image_url        — full resolution served directly
  //   3. preview_data_url — inline base64 JPEG thumbnail (image_preview)
  //   4. preview_image_url — local:// placeholder, skipped
  // ``resolveAssetUrl`` prefixes /local-assets/* with AGENT_BASE_URL,
  // skips ``local://`` URIs, and passes ``data:`` URIs through.
  const imageSrc = [
    ad?.asset_url,
    ad?.image_url,
    ad?.preview_data_url,
    ad?.preview_image_url,
  ]
    .map(resolveAssetUrl)
    .find((u): u is string => !!u);

  return (
    <div
      className={clsx(
        'relative rounded-lg border bg-white overflow-hidden transition-shadow',
        isFailed
          ? 'border-rose-200'
          : isFinal
            ? 'border-emerald-200 hover:shadow-md cursor-pointer'
            : 'border-slate-200',
        onClick ? 'cursor-pointer' : '',
      )}
      onClick={onClick}
    >
      {/* Image area (1:1 aspect) — hydrates as briefs/images/reviews complete */}
      <div
        className={clsx(
          'relative aspect-square bg-gradient-to-br from-slate-50 to-slate-100',
          compact ? 'min-h-[120px]' : 'min-h-[160px]',
        )}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={ad?.headline ?? `Ad ${slotIdx + 1}`}
            className={clsx(
              'absolute inset-0 w-full h-full object-cover',
              hasImage || hasThumbnail ? 'opacity-100' : 'opacity-60',
            )}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {hasPreview ? (
              <Loader2 size={24} className="text-violet-400 animate-spin" />
            ) : (
              <ImageIcon size={24} className="text-slate-300" />
            )}
          </div>
        )}

        {/* Shimmer overlay during preview state */}
        {state === 'image_preview' && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
        )}

        {/* Failed overlay */}
        {isFailed && (
          <div className="absolute inset-0 bg-rose-50/90 flex flex-col items-center justify-center p-3 text-center">
            <AlertCircle size={20} className="text-rose-500 mb-1" />
            <p className="text-xs text-rose-700 font-medium">Failed</p>
            {ad?.error && (
              <p className="text-[10px] text-rose-500 mt-1 line-clamp-2">{ad.error}</p>
            )}
          </div>
        )}

        {/* Review score badge */}
        {ad?.review_score != null && !isFailed && (
          <div
            className={clsx(
              'absolute top-2 right-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold border',
              ad.review_pass
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-amber-50 text-amber-700 border-amber-200',
            )}
          >
            {ad.review_score.toFixed(1)}
          </div>
        )}
      </div>

      {/* Text area (skeleton until brief_ready) */}
      <div className="p-2 space-y-1">
        <div className="flex items-center justify-between gap-1">
          <span className="text-[10px] text-slate-400 font-mono">#{slotIdx + 1}</span>
          <span
            className={clsx(
              'text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full border',
              STATE_BADGE_CLS[state],
            )}
          >
            {STATE_LABEL[state]}
          </span>
        </div>
        {ad?.headline ? (
          <p className="text-xs font-semibold text-slate-700 line-clamp-2">{ad.headline}</p>
        ) : (
          <div className="h-3 bg-slate-100 rounded animate-pulse w-3/4" />
        )}
        {ad?.hook ? (
          <p className="text-[11px] text-slate-500 line-clamp-2">{ad.hook}</p>
        ) : (
          <div className="h-2 bg-slate-100 rounded animate-pulse w-1/2" />
        )}
      </div>
    </div>
  );
}

export default LiveAdPackView;
